import React, { memo, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";

import {
  Avatar,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ParkIcon from "@mui/icons-material/Park";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useLocation, useNavigate } from "react-router";
import { TypesAction } from "../../redux/typesAction";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux";
import { theme } from "../../utils/theme";
import { ButtonLogin } from "./header.style";
import "./style.scss";
import { BASE_URL, HttpMethod, PATH_IMAGE_URL } from "../../services/api.constant";
import { callHttpClientMethod } from "../../services/callHttpClient";
import { logout } from "../../services/apiList";
import Noti from "./Noti";
import Search from "./Seach";

const MENU_LINKS = [
  { text: "ABOUT US", path: "/about-us", icon: ApartmentIcon },
  { text: "PLANTS", path: "/products-plants", icon: ParkIcon },
  { text: "POTS", path: "/products-pots", icon: TakeoutDiningIcon },
] as const;

const drawerPaperSx = {
  width: { xs: "min(300px, 92vw)", sm: 300 },
  height: "100%",
  maxHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "#fff",
  boxSizing: "border-box",
} as const;

/** Căn giữa icon – chữ (MUI ListItemText hay thêm margin Typography). */
const menuListItemIconSx = {
  minWidth: 40,
  width: 40,
  height: 40,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;

const menuListItemButtonSx = {
  alignItems: "center",
  borderRadius: 2,
  py: 1.25,
  px: 1.5,
} as const;

const MenuMobile: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { is_show_input } = useSelector((state: RootState) => state.products);
  const { user, auth } = useSelector((state: RootState) => state.auth);
  const { tokenPush } = useSelector((state: RootState) => state.listNotification);
  const { carts } = useSelector((state: RootState) => state.cart);
  const [showTab, setShowTab] = useState(false);

  const avatarSrc = React.useMemo(() => {
    const raw = user?.avatar;
    if (!raw) return "";
    if (typeof raw === "string" && raw.startsWith("http")) return raw;
    const normalized = String(raw).replace(/^\//, "");
    return `${PATH_IMAGE_URL}${normalized}`;
  }, [user?.avatar]);

  const navigateMenu = useCallback(
    (path: string) => {
      navigate(path);
      if (path === "/products-plants") {
        dispatch({ type: TypesAction.CATEGORY_ID, payload: 1 });
      }
      if (path === "/products-pots") {
        dispatch({ type: TypesAction.CATEGORY_ID, payload: 2 });
      }
      setShowTab(false);
    },
    [dispatch, navigate]
  );

  const handleLogout = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setShowTab(false);
      if (!auth?.access_token) return;
      try {
        await callHttpClientMethod(
          BASE_URL + logout(),
          HttpMethod.Post,
          { token_push: tokenPush },
          {
            headers: {
              Authorization: `Bearer ${auth.access_token}`,
            },
          }
        );
      } catch {
        /* vẫn xóa session phía client */
      }
      dispatch({ type: TypesAction.LOGOUT });
      dispatch({ type: TypesAction.VERIFYOTP, payload: user?.email });
      navigate("/login");
    },
    [auth?.access_token, dispatch, navigate, tokenPush, user?.email]
  );

  const handleClickCart = () => {
    navigate(auth?.access_token ? "/cart" : "/login");
  };

  const drawerContent = (
    <Box
      role="presentation"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: "100%",
      }}
    >
      {auth?.access_token ? (
        <>
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1.5,
              borderBottom: "1px solid #ececec",
            }}
          >
            <ListItemButton
              onClick={() => navigateMenu("/account")}
              sx={{
                ...menuListItemButtonSx,
                "&:hover": { bgcolor: "rgba(0, 127, 24, 0.06)" },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 52,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Avatar
                  alt=""
                  src={avatarSrc || undefined}
                  sx={{ width: 44, height: 44, border: "1px solid #e0e0e0" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={(user?.full_name || "Account").toUpperCase()}
                sx={{ my: 0 }}
                primaryTypographyProps={{
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  letterSpacing: "0.08em",
                  color: "#007f18",
                  lineHeight: 1.25,
                  sx: { m: 0 },
                }}
              />
            </ListItemButton>
          </Box>

          <List sx={{ flex: 1, py: 1, px: 1, overflow: "auto" }}>
            {MENU_LINKS.map(({ text, path, icon: Icon }) => {
              const active = location.pathname === path;
              return (
                <ListItem key={path} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => navigateMenu(path)}
                    sx={{
                      ...menuListItemButtonSx,
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        ...menuListItemIconSx,
                        color: active ? "#007f18" : theme.palette.text.body,
                      }}
                    >
                      <Icon sx={{ fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={text}
                      sx={{ my: 0, display: "flex", alignItems: "center" }}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        letterSpacing: "0.06em",
                        color: active ? "#007f18" : theme.palette.text.body,
                        lineHeight: 1.25,
                        sx: { m: 0 },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Box
            sx={{
              mt: "auto",
              flexShrink: 0,
              p: 2,
              pt: 1.5,
              borderTop: "1px solid #ececec",
              bgcolor: "#fafafa",
            }}
          >
            <Button
              fullWidth
              type="button"
              variant="outlined"
              size="medium"
              startIcon={<LogoutIcon sx={{ fontSize: 20 }} />}
              onClick={handleLogout}
              sx={{
                py: 1.25,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
                borderColor: "rgba(0,0,0,0.14)",
                color: "#5c5c5c",
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#b71c1c",
                  color: "#b71c1c",
                  bgcolor: "rgba(183, 28, 28, 0.06)",
                },
              }}
            >
              Log out
            </Button>
          </Box>
        </>
      ) : (
        <List sx={{ py: 1, px: 1 }}>
          <ListItem disablePadding sx={{ mb: 2, justifyContent: "center" }}>
            <ButtonLogin className="btn_login_mobile" to="/login" onClick={() => setShowTab(false)}>
              LOGIN
            </ButtonLogin>
          </ListItem>
          {MENU_LINKS.map(({ text, path, icon: Icon }) => {
            const active = location.pathname === path;
            return (
              <ListItem key={path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => navigateMenu(path)}
                  sx={{
                    ...menuListItemButtonSx,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      ...menuListItemIconSx,
                      color: active ? "#007f18" : theme.palette.text.body,
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    sx={{ my: 0, display: "flex", alignItems: "center" }}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      letterSpacing: "0.04em",
                      color: active ? "#007f18" : theme.palette.text.body,
                      textTransform: "capitalize",
                      lineHeight: 1.25,
                      sx: { m: 0 },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: { xs: "flex", md: "none" },
        flex: 1,
        minWidth: 0,
        maxWidth: "100%",
        alignItems: "center",
        gap: { xs: 0.25, sm: 0.5 },
        minHeight: 44,
        pl: 0.5,
        pr: 0.5,
      }}
    >
      {is_show_input && (
        <Box
          sx={{
            flex: "1 1 0%",
            minWidth: 0,
            maxWidth: "100%",
            display: { xs: "block", sm: "none" },
            overflow: "hidden",
            pr: 0.5,
          }}
        >
          <Search />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.25,
          flexShrink: 0,
          flexGrow: 0,
          ...(is_show_input ? {} : { ml: "auto" }),
        }}
      >
        {auth?.access_token && (
          <Box sx={{ display: "flex", flexShrink: 0 }}>
            <Noti />
          </Box>
        )}
        <Box
          onClick={handleClickCart}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleClickCart();
          }}
          aria-label="Cart"
          sx={{
            position: "relative",
            cursor: "pointer",
            color:
              location.pathname === "/cart"
                ? "#007F18"
                : theme.palette.text.body,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {carts.length !== 0 && (
            <Box
              sx={{
                minWidth: { xs: 15, sm: 18 },
                height: { xs: 15, sm: 18 },
                px: { xs: 0.375, sm: 0.5 },
                backgroundColor: "red",
                position: "absolute",
                top: { xs: -2, sm: -4 },
                right: { xs: -2, sm: 0 },
                borderRadius: 999,
                color: "white",
                fontSize: { xs: 9, sm: 10 },
                fontWeight: 600,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {carts.length}
            </Box>
          )}
          <ShoppingCartOutlinedIcon
            sx={{ width: { xs: 26, sm: 38 }, height: { xs: 26, sm: 38 } }}
          />
        </Box>

        <IconButton
          color="inherit"
          aria-label="Open menu"
          edge="end"
          onClick={() => {
            setShowTab(true);
          }}
          sx={{
            flexShrink: 0,
            p: 0.5,
            color: "text.primary",
          }}
        >
          <MenuIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
        </IconButton>
      </Box>
      <Drawer
        sx={{ zIndex: 99999 }}
        anchor="right"
        open={showTab}
        onClose={() => setShowTab(false)}
        PaperProps={{ sx: drawerPaperSx }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};
export default memo(MenuMobile);
